import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core'
import {LinkService, OTHER_OFFERING_ID} from '../../../../core/i18n/link.service'
import {PUBLIC_ROUTES} from '../../../../shared/routing/public.routes'
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms'
import {ActivatedRoute, Router} from '@angular/router'
import {map, startWith, takeUntil, tap} from 'rxjs/operators'
import {Observable, Subject} from 'rxjs'
import {userFriendlyFormatDateToISO} from '../../../../core/date/date.utils'
import {
  JobRequestDetail_CalculatePriceRangeGql,
  OfferingType,
  RequestJobError,
} from '../../../../../__generated__/types'
import {
  JobRequestBillingDetails,
  JobRequestContact,
  JobRequestDamage,
  JobRequestDetail,
  JobRequestExecutionDetails,
  JobRequestFiles,
  JobRequestOffering,
  JobRequestRepository,
  JobRequestState,
  PhotosAnchorTag,
  VoucherAnchorTag,
} from '../job-request.repository'
import {DynamicQuestionSummary} from '../../../../shared/components/dynamic-form/dynamic-form-summary/dynamic-form-summary.component'
import {faCalendarCheck} from '@fortawesome/pro-regular-svg-icons/faCalendarCheck'
import {faEnvelope} from '@fortawesome/pro-regular-svg-icons/faEnvelope'
import {faFolderOpen} from '@fortawesome/pro-solid-svg-icons/faFolderOpen'
import {faMapMarkerAlt} from '@fortawesome/pro-solid-svg-icons/faMapMarkerAlt'
import {faPhone} from '@fortawesome/pro-solid-svg-icons/faPhone'
import {FooterProgressSteps} from '../job-request-footer/job-request-footer.component'
import {atLeastOneOptionSelected} from '../../../../core/forms/at-least-one-selected.validator'
import {ProcessService} from '../../../../shared/components/process-navbar/process.service'
import {PriceRange} from '../../../../craft/price-estimation/price-range.types'
import {OfferingResolved} from '../resolvers/job-request-offering.resolver'
import {AgentResolved} from '../resolvers/job-request-agent.resolver'
import {FormService} from '../../../../core/forms/form.service'
import {JobRequestService} from '../job-request.service'
import {indicate} from '../../../../core/graphql/indicate'
import {AgencyResolved} from '../resolvers/job-request-agency.resolver'
import {MoneyService} from '../../../../core/forms/money.service'
import {AnalyticsService} from '../../../../core/analytics/analytics.service'
import {QueryParamsService} from '../../../../core/query-params/query-params.service'
import {conditionalValidator, isControlInvalid} from '../../../../craft/forms/form.types'
import {PageComponent, PageMetadata} from '../../../../core/seo/page-metadata'
import {JobRequestPageMetadataService} from '../job-request-page-metadata.service'
import {faTicketPerforated} from '@fortawesome/pro-regular-svg-icons/faTicketPerforated'
import {faImages} from '@fortawesome/pro-solid-svg-icons/faImages'
import {Logger} from '../../../../core/logging/logger.types'

@Component({
  templateUrl: './job-request-summary-page.component.html',
  styleUrls: ['./job-request-summary-page.component.css'],
})
export class JobRequestSummaryPageComponent extends PageComponent implements OnInit, OnDestroy {
  readonly nextPath = PUBLIC_ROUTES.JOB_REQUEST_SUCCESS
  readonly editContact = PUBLIC_ROUTES.JOB_REQUEST_CONTACT
  readonly editDetail = PUBLIC_ROUTES.JOB_REQUEST_DETAIL
  readonly editTimeFrame = PUBLIC_ROUTES.JOB_REQUEST_EXECUTION_TIME
  readonly previousPath = this.editContact
  readonly APPLICATION_ROUTES = PUBLIC_ROUTES
  readonly calendarIcon = faCalendarCheck
  readonly addressIcon = faMapMarkerAlt
  readonly phoneIcon = faPhone
  readonly emailIcon = faEnvelope
  readonly damageIcon = faFolderOpen
  readonly voucherIcon = faTicketPerforated
  readonly filesIcon = faImages
  readonly RequestJobError = RequestJobError
  readonly offering?: OfferingResolved = this.route.snapshot.data['offering']
  readonly agent?: AgentResolved = this.route.snapshot.data['agent']
  readonly agency?: AgencyResolved = this.route.snapshot.data['agency']

  readonly clientExpectationControls = {
    consulting: new UntypedFormControl(false),
    estimation: new UntypedFormControl(false),
    offer: new UntypedFormControl(false),
    execution: new UntypedFormControl(false),
  }
  readonly agentExpectationControls = {
    offerValidationType: new UntypedFormControl(
      undefined,
      conditionalValidator(() => this.formConfig?.shouldAskAgentExpectation === true, [Validators.required]),
    ),
    deductible: new UntypedFormControl(200),
    coverage: new UntypedFormControl(
      undefined,
      conditionalValidator(
        () => this.agentExpectationControls?.offerValidationType.value === 'AUTO_DAMAGE_COVERED',
        [Validators.required],
      ),
    ),
  }
  readonly controls = {
    clientExpectation: new UntypedFormGroup(
      this.clientExpectationControls,
      conditionalValidator(() => this.formConfig?.shouldAskClientExpectation === true, [atLeastOneOptionSelected()]),
    ),
    damageAgentExpectation: new UntypedFormGroup(this.agentExpectationControls),
  }
  readonly form = this.fb.group(this.controls)

  jobRequestState!: JobRequestState
  questionSummary: DynamicQuestionSummary[] = []
  priceRange$?: Observable<PriceRange | undefined>
  formConfig?: {
    canSkipTermsAndConditions: boolean
    shouldAskClientExpectation: boolean
    shouldAskAgentExpectation: boolean
  }

  submitJobRequestError: RequestJobError | undefined
  readonly isRequestingJob$ = new Subject<boolean>()

  private readonly controlsForProgressCalculation = [this.form]
  readonly progressSteps: FooterProgressSteps = {
    currentStep$: this.form.valueChanges.pipe(
      startWith(this.form),
      map(() => {
        return this.controlsForProgressCalculation.filter(c => c.valid).length
      }),
    ),
    numberOfSteps: this.controlsForProgressCalculation.length,
  }

  private readonly unsubscribe$ = new Subject<void>()

  constructor(
    readonly linkService: LinkService,
    readonly processService: ProcessService,
    readonly formService: FormService,
    private readonly fb: UntypedFormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly repository: JobRequestRepository,
    private readonly jobRequestService: JobRequestService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly calculatePriceRangeGql: JobRequestDetail_CalculatePriceRangeGql,
    private readonly analyticsService: AnalyticsService,
    private readonly queryParamsService: QueryParamsService,
    private readonly jobRequestPageMetadataService: JobRequestPageMetadataService,
    private readonly logger: Logger,
  ) {
    super()
    this.jobRequestState = this.getSummaryState()
    this.formConfig = this.getConfigState(this.jobRequestState.damage)
  }

  getPageMetadata(): Observable<PageMetadata> {
    return this.jobRequestPageMetadataService.getPageMetadataForStep(
      'SUMMARY',
      this.offering || {label: this.jobRequestState.detail.title || ''},
    )
  }

  get numberOfItems(): number | undefined {
    return this.jobRequestState?.offering?.staticQuestions?.installLamps?.numberOfLamps
  }

  ngOnInit(): void {
    // because coverage validation depends on offerValidationType, update its validity when offerValidationType changes
    // otherwise we might get an invalid coverage even though it's no longer checked
    this.agentExpectationControls.offerValidationType.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.agentExpectationControls.coverage.updateValueAndValidity()
    })
  }

  private getSummaryState() {
    const executionDetails = this.repository.readJobRequestExecutionState() as JobRequestExecutionDetails
    const documentState = this.repository.readJobRequestFilesState() as JobRequestFiles
    const offering = this.repository.readJobRequestOfferingState()
    const detail = this.repository.readJobRequestDetailState() as JobRequestDetail
    const damage = this.repository.readJobRequestDamageState()
    const stakeholder = this.repository.readJobRequestStakeholderState()
    const preferredPro = this.repository.readJobRequestPreferredProState()
    const intentionSlug = this.repository.getIntentionSlug()
    const clientExpectation = this.repository.readClientExpectationState()

    this.questionSummary = this.createQuestionSummary(offering)
    if (offering?.staticQuestions && Object.values(offering?.staticQuestions).some(value => !!value)) {
      this.priceRange$ = this.calculatePriceRangeGql
        .fetch({
          jobQuestions: offering.staticQuestions,
        })
        .pipe(map(value => value.data.calculatePriceRange))
    }

    return {
      contact: this.repository.readJobRequestContactState() as JobRequestContact,
      billingDetails: this.repository.readJobRequestBillingDetailsState() as JobRequestBillingDetails,
      clientExpectation,
      detail: {
        voucherCode: detail.voucherCode,
        referrer: detail.referrer,
        description: detail.description,
        title: detail.title || (this.offering?.label ?? ''),
      },
      damage,
      executionDetails: {
        ...executionDetails,
        ...(executionDetails?.latestPossibleDate && {
          latestPossibleDate: userFriendlyFormatDateToISO(executionDetails.latestPossibleDate),
        }),
      },
      attachedFiles: documentState,
      offering: offering
        ? {
            ...offering,
            intentionSlug,
          }
        : undefined,
      stakeholder,
      preferredPro,
    }
  }

  private getConfigState(damage?: JobRequestDamage) {
    return {
      canSkipTermsAndConditions: this.jobRequestService.canSkipTermsAndConditions({damage}),
      shouldAskClientExpectation:
        this.jobRequestService.shouldAskClientExpectation({offering: this.offering, damage}) &&
        !this.jobRequestState.clientExpectation,
      shouldAskAgentExpectation: this.jobRequestService.shouldAskAgentExpectation({damage}),
    }
  }

  private createQuestionSummary(offering: JobRequestOffering | undefined) {
    const requestedOffering = this.offering
    if (!requestedOffering || !offering) {
      return []
    }

    return offering.questions
      .filter(q => q.answerValue || q.answerIds?.length)
      .map(q => {
        const question = requestedOffering.questions?.find(_ => _.id === q.id)
        if (!question) {
          this.logger.warn(`Question ${q.id} was not found`)
          return undefined
        }

        let answers: string[]
        if (q.answerIds?.length) {
          answers = question.possibleAnswers!.filter(_ => q.answerIds!.includes(_.id)).map(_ => _.label!)
        } else {
          answers = [q.answerValue!]
        }

        const summary: DynamicQuestionSummary = {
          label: question.proFacingLabel,
          answers,
          type: question.type,
          rank: question.rank,
          additionalInformation: q.additionalInformation,
        }
        return summary
      })
      .filter((q): q is DynamicQuestionSummary => !!q)
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  requestJob(): void {
    this.form.markAllAsTouched()
    if (this.form.valid && this.jobRequestState) {
      if (this.formConfig?.shouldAskClientExpectation) {
        this.jobRequestState.clientExpectation = this.controls.clientExpectation.getRawValue()
      }
      if (this.formConfig?.shouldAskAgentExpectation) {
        const agentExpectation = this.controls.damageAgentExpectation.getRawValue()
        this.jobRequestState.damageAgentExpectation = {
          ...agentExpectation,
          deductible: agentExpectation.deductible
            ? MoneyService.chfAmountToRappen(agentExpectation.deductible)
            : undefined,
          coverage: agentExpectation.coverage ? MoneyService.chfAmountToRappen(agentExpectation.coverage) : undefined,
        }
      }

      const queryParams = this.queryParamsService.consumeQueryParams()
      if (queryParams) {
        this.jobRequestState.analyticParams = queryParams
      }

      this.jobRequestService
        .requestJob(this.jobRequestState)
        .pipe(
          indicate(this.isRequestingJob$),
          tap(() => {
            this.enhanceConversion()
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(res => {
          if (res.__typename === 'Job') {
            this.submitJobRequestError = undefined
            this.linkService.navigateTo(this.nextPath, {
              routeParams: {
                id: res.id,
                offeringId: this.jobRequestState.offering?.id || OTHER_OFFERING_ID,
              },
            })
          } else if (res.__typename === 'RequestJobUserInputError') {
            this.submitJobRequestError = res.error
            this.cdr.markForCheck()
          } else {
            this.logger.error('Unhandled response', res)
          }
        })
    }
  }

  previous(): void {
    this.router.navigate([this.previousPath.routingPath], {
      relativeTo: this.route.parent,
      queryParamsHandling: 'merge',
    })
  }

  enhanceConversion() {
    const contact = this.jobRequestState.contact
    const address = this.jobRequestState.executionDetails.executionAddress
    this.analyticsService.hashAndSendEnhancedConversionInfo({
      clientEmail: contact.email,
      clientPhone: contact.phoneNumber,
      clientAddress: {
        clientFirstName: contact.firstName,
        clientLastName: contact.lastName,
        clientPostalCode: `${address?.postalCode}`,
        clientCountry: 'Switzerland',
      },
    })
  }

  protected readonly isControlInvalid = isControlInvalid
  protected readonly OfferingType = OfferingType
  protected readonly VoucherAnchorTag = VoucherAnchorTag
  protected readonly PhotosAnchorTag = PhotosAnchorTag
}
